// Путь: D:\QR\QR-WEB\dashboard\src\components\Admin\AdminVouchers\AdminVouchers.jsx
// Версия: 2.0.0 - Button filters, improved UX
// Дата: 03.12.2025

import React, { useState, useEffect } from 'react';
import './AdminVouchers.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://qr-money.onrender.com';

const THEMES = [
  { id: 'all', emoji: '📋', title: 'Все' },
  { id: 'default', emoji: '🎫', title: 'Обычный' },
  { id: 'birthday', emoji: '🎂', title: 'День рождения' },
  { id: 'love', emoji: '❤️', title: 'Любовь' },
  { id: 'thanks', emoji: '🙏', title: 'Спасибо' },
  { id: 'gift', emoji: '🎁', title: 'Подарок' },
  { id: 'congrats', emoji: '🎉', title: 'Поздравляю' },
  { id: 'coffee', emoji: '☕', title: 'Кофе' },
  { id: 'lunch', emoji: '🍽️', title: 'Обед' },
  { id: 'travel', emoji: '✈️', title: 'Путешествие' },
  { id: 'gaming', emoji: '🎮', title: 'Игры' },
  { id: 'shopping', emoji: '🛍️', title: 'Шоппинг' },
  { id: 'newyear', emoji: '🎄', title: 'Новый год' },
];

const STATUSES = [
  { id: 'all', label: 'Все', icon: '📋', color: '' },
  { id: 'active', label: 'Активные', icon: '🟢', color: 'green' },
  { id: 'claimed', label: 'Использованные', icon: '✅', color: 'blue' },
  { id: 'expired', label: 'Истекшие', icon: '🔴', color: 'red' },
  { id: 'cancelled', label: 'Отменённые', icon: '⚫', color: 'gray' },
];

const AdminVouchers = () => {
  const [vouchers, setVouchers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [themeFilter, setThemeFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const token = localStorage.getItem('authToken');

  useEffect(() => {
    fetchVouchers();
    fetchStats();
  }, [statusFilter, themeFilter, page]);

  const fetchVouchers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page, limit: 15 });
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (themeFilter !== 'all') params.append('theme', themeFilter);

      const res = await fetch(`${API_URL}/api/admin/vouchers?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setVouchers(data.data || []);
        setTotalPages(data.pagination?.totalPages || 1);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/vouchers/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setStats(data.data || {});
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить подарок?')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/vouchers/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        fetchVouchers();
        fetchStats();
      }
    } catch (error) {
      alert('Ошибка: ' + error.message);
    }
  };

  const handleCleanup = async () => {
    if (!window.confirm(`Очистить ${stats.expired || 0} истекших подарков и вернуть средства создателям?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/vouchers/cleanup`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        alert(`Очищено: ${data.data?.processed || 0} подарков`);
        fetchVouchers();
        fetchStats();
      }
    } catch (error) {
      alert('Ошибка: ' + error.message);
    }
  };

  const exportCSV = () => {
    if (vouchers.length === 0) return alert('Нет данных для экспорта');
    
    const headers = ['ID', 'Создатель', 'Сумма', 'Тема', 'Статус', 'Создан'];
    const rows = vouchers.map(v => [
      v.id,
      v.creator_email || '-',
      parseFloat(v.amount).toFixed(2),
      v.theme_name || 'default',
      v.status,
      new Date(v.created_at).toLocaleString('ru-RU')
    ]);
    
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `vouchers_${new Date().toISOString().slice(0,10)}.csv`;
    link.click();
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('ru-RU', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const s = STATUSES.find(st => st.id === status) || {};
    return <span className={`admin-status-badge ${status}`}>{s.icon} {s.label}</span>;
  };

  const getThemeEmoji = (themeName) => {
    const t = THEMES.find(th => th.id === themeName);
    return t ? t.emoji : '🎫';
  };

  return (
    <div className="admin-vouchers">
      <div className="admin-page-header">
        <h1 className="admin-page-title">🎁 Управление подарками</h1>
        <div className="admin-header-actions">
          <button className="admin-btn" onClick={exportCSV}>📥 Экспорт CSV</button>
          {stats.expired > 0 && (
            <button className="admin-btn admin-btn-danger" onClick={handleCleanup}>
              🧹 Очистить истекшие ({stats.expired})
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="admin-voucher-stats">
        <div className="admin-vstat" onClick={() => setStatusFilter('all')}>
          <span className="admin-vstat-value">{stats.total || 0}</span>
          <span className="admin-vstat-label">Всего</span>
        </div>
        <div className="admin-vstat green" onClick={() => setStatusFilter('active')}>
          <span className="admin-vstat-value">{stats.active || 0}</span>
          <span className="admin-vstat-label">Активных</span>
        </div>
        <div className="admin-vstat blue" onClick={() => setStatusFilter('claimed')}>
          <span className="admin-vstat-value">{stats.claimed || 0}</span>
          <span className="admin-vstat-label">Использовано</span>
        </div>
        <div className="admin-vstat red" onClick={() => setStatusFilter('expired')}>
          <span className="admin-vstat-value">{stats.expired || 0}</span>
          <span className="admin-vstat-label">Истекло</span>
        </div>
        <div className="admin-vstat gold">
          <span className="admin-vstat-value">${parseFloat(stats.total_amount || 0).toFixed(2)}</span>
          <span className="admin-vstat-label">Общая сумма</span>
        </div>
      </div>

      {/* Status Filter */}
      <div className="admin-filter-section">
        <label>Статус:</label>
        <div className="admin-filter-buttons">
          {STATUSES.map(s => (
            <button
              key={s.id}
              className={`admin-filter-btn ${statusFilter === s.id ? 'active' : ''} ${s.color}`}
              onClick={() => { setStatusFilter(s.id); setPage(1); }}
            >
              {s.icon} {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Theme Filter */}
      <div className="admin-filter-section">
        <label>Тема:</label>
        <div className="admin-filter-buttons themes">
          {THEMES.map(t => (
            <button
              key={t.id}
              className={`admin-filter-btn theme ${themeFilter === t.id ? 'active' : ''}`}
              onClick={() => { setThemeFilter(t.id); setPage(1); }}
              title={t.title}
            >
              {t.emoji}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="admin-card">
        {loading ? (
          <div className="admin-loading">Загрузка...</div>
        ) : vouchers.length === 0 ? (
          <div className="admin-empty">Подарки не найдены</div>
        ) : (
          <>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Создатель</th>
                  <th>Получатель</th>
                  <th>Сумма</th>
                  <th>Тема</th>
                  <th>Статус</th>
                  <th>Создан</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {vouchers.map(v => (
                  <tr key={v.id}>
                    <td>#{v.id}</td>
                    <td>{v.creator_email || '-'}</td>
                    <td>{v.recipient_email || '-'}</td>
                    <td><strong>${parseFloat(v.amount).toFixed(2)}</strong></td>
                    <td><span className="admin-theme-cell">{getThemeEmoji(v.theme_name)} {v.theme_name || 'default'}</span></td>
                    <td>{getStatusBadge(v.status)}</td>
                    <td>{formatDate(v.created_at)}</td>
                    <td>
                      {(v.status === 'expired' || v.status === 'cancelled') && (
                        <button 
                          className="admin-btn admin-btn-danger admin-btn-sm"
                          onClick={() => handleDelete(v.id)}
                          title="Удалить"
                        >
                          🗑️
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="admin-pagination">
                <button 
                  className="admin-btn"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  ← Назад
                </button>
                <span className="admin-page-info">Страница {page} из {totalPages}</span>
                <button 
                  className="admin-btn"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Вперёд →
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminVouchers;